import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import apiClient from "../../../utils/apiClient";
import apiClientChoice from "../../../utils/apiClientChoice";
import { Input } from "../../../components/ui/input";
import { Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import server from "../../../utils/serverConfig";

const statusOptions = [
  { value: "active", label: "active" },
  { value: "inactive", label: "inactive" },
  { value: "deactivated", label: "deactivated" },
  { value: "dummy", label: "dummy" },
];

const accountStatusOptions = [
  { value: "ACTIVE", label: "ACTIVE" },
  { value: "INACTIVE", label: "INACTIVE" },
];

const labelOptions = [
  { value: "clearmind", label: "clearmind" },
  { value: "satyam", label: "satyam" },
];

export function NubraUserForm({ 
  handleAddNubraUserIndexMuliplier, 
  onClose, 
  proxyData, 
  isOnholdLegality, 
  isLegalitiesComplete, 
  isPolarisOrPolarisLite, 
  isWizard = false,
  onWizardSubmit,
  prefilledName = "",
  prefilledPhone = "",
  prefilledEmail = "",
  prefilledDob = "",
  prefilledClientId = "",
  prefilledTotpKey = "",
  prefilledPin = "",
  prefilledPassword = "",
  prefilledLabel = "",
  prefilledSizing = 0,
  prefilledAccountStatus = "INACTIVE",
  prefilledProxyUser = "",
  prefilledProxyPassword = "",
  prefilledProxyHost = "",
  prefilledProxyPort = 0,
  prefilledProxyStaticIp = "",
  prefilledProxyToggle = false
}) {
  // ── Common states ────────────────────────────────────────────────────────
  const [clientId, setClientId] = useState(prefilledClientId || "");
  const [name, setName] = useState(prefilledName || "");
  const [totpKey, setTotpKey] = useState(prefilledTotpKey || "");
  const [pin, setPin] = useState(prefilledPin || "");
  const [email, setEmail] = useState(prefilledEmail || "");
  const [status, setStatus] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(prefilledPhone || "");
  const [label, setLabel] = useState(prefilledLabel || "");

  // ── Polaris-only states ──────────────────────────────────────────────────
  const [password, setPassword] = useState(prefilledPassword || "");
  const [dob, setDob] = useState(prefilledDob || "");
  const [sizing, setSizing] = useState(prefilledSizing || 0);
  const [accountStatus, setAccountStatus] = useState(prefilledAccountStatus || "INACTIVE");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Multiplier states ─────────────────────────────────────────────────────
  const [multiplier, setMultiplier] = useState(1);
  const [bankniftyMultiplier, setBankniftyMultiplier] = useState(0);
  const [finniftyMultiplier, setFinniftyMultiplier] = useState(0);
  const [midcpniftyMultiplier, setMidcpniftyMultiplier] = useState(0);
  const [sensexMultiplier, setSensexMultiplier] = useState(0);

  // ── Proxy states ─────────────────────────────────────────────────────────
  const [proxyUser, setProxyUser] = useState(prefilledProxyUser || "");
  const [proxyPassword, setProxyPassword] = useState(prefilledProxyPassword || "");
  const [proxyHost, setProxyHost] = useState(prefilledProxyHost || "");
  const [proxyPort, setProxyPort] = useState(prefilledProxyPort || 0);
  const [proxyToggle, setProxyToggle] = useState(prefilledProxyToggle || false);
  const [proxyStaticIp, setProxyStaticIp] = useState(prefilledProxyStaticIp || "");

  // ── Sync prefilled props asynchronously ───────────────────────────────────
  useEffect(() => {
    if (prefilledClientId) setClientId(prefilledClientId);
    if (prefilledName) setName(prefilledName);
    if (prefilledPhone) setPhoneNumber(prefilledPhone);
    if (prefilledTotpKey) setTotpKey(prefilledTotpKey);
    if (prefilledPin) setPin(prefilledPin);
    if (prefilledEmail) setEmail(prefilledEmail);
    if (prefilledLabel) setLabel(prefilledLabel);
    if (prefilledPassword) setPassword(prefilledPassword);
    if (prefilledDob) setDob(prefilledDob);
    if (prefilledSizing !== undefined && prefilledSizing !== null) setSizing(prefilledSizing);
    if (prefilledAccountStatus) setAccountStatus(prefilledAccountStatus);
    if (prefilledProxyUser) setProxyUser(prefilledProxyUser);
    if (prefilledProxyPassword) setProxyPassword(prefilledProxyPassword);
    if (prefilledProxyHost) setProxyHost(prefilledProxyHost);
    if (prefilledProxyPort !== undefined && prefilledProxyPort !== null) setProxyPort(prefilledProxyPort);
    if (prefilledProxyStaticIp) setProxyStaticIp(prefilledProxyStaticIp);
    if (prefilledProxyToggle !== undefined && prefilledProxyToggle !== null) setProxyToggle(prefilledProxyToggle);
  }, [
    prefilledClientId, prefilledName, prefilledPhone, prefilledTotpKey,
    prefilledPin, prefilledEmail, prefilledLabel, prefilledPassword, prefilledDob,
    prefilledSizing, prefilledAccountStatus, prefilledProxyUser,
    prefilledProxyPassword, prefilledProxyHost, prefilledProxyPort,
    prefilledProxyStaticIp, prefilledProxyToggle
  ]);

  // ── Visibility states ─────────────────────────────────────────────────────
  const [showTotpKey, setShowTotpKey] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showProxyPassword, setShowProxyPassword] = useState(false);

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOnholdLegality && !isLegalitiesComplete) {
      setStatus("inactive");
      setAccountStatus("INACTIVE");
    }
  }, [isOnholdLegality, isLegalitiesComplete]);

  useEffect(() => {
    if (isPolarisOrPolarisLite) {
      setMultiplier(0);
      setBankniftyMultiplier(0);
      setFinniftyMultiplier(0);
      setMidcpniftyMultiplier(0);
      setSensexMultiplier(0);
    }
  }, [isPolarisOrPolarisLite]);

  useEffect(() => {
    if (proxyData) {
      setProxyUser(proxyData.proxy_user || "");
      setProxyPassword(proxyData.proxy_password || "");
      setProxyHost(proxyData.proxy_host || "");
      setProxyPort(proxyData.proxy_port || 0);
      setProxyStaticIp(proxyData.proxy_static_ip || "");
      setProxyToggle(true);
    }
  }, [proxyData]);

  // ── Polaris (Choice-server) submit ────────────────────────────────────────
  const handleAddPolarisChoiceNubraUser = () => {
    const cId = (clientId || "").trim();
    const nm = (name || "").trim();
    const em = (email || "").trim();
    const totp = (totpKey || "").trim();
    const pwd = (password || "").trim();
    const ph = (phoneNumber || "").trim();
    const d = (dob || "").trim();
    const pUser = (proxyUser || "").trim();
    const pPwd = (proxyPassword || "").trim();
    const pHost = (proxyHost || "").trim();
    const pStaticIp = (proxyStaticIp || "").trim();

    if (!cId || !nm || !em || !totp || !pwd) {
      toast.error("Missing required fields!", { duration: 5000 });
      return;
    }

    const payload = {
      name: nm,
      email: em,
      password: pwd,
      phone: ph,
      client_id: cId,
      active: accountStatus === "ACTIVE",
      broker: "NUBRA",
      multiplier: "0",
      dob: d,
      totp_key: totp,
      proxy_user: pUser,
      proxy_password: pPwd,
      proxy_host_db: pHost,
      proxy_port: parseInt(proxyPort) || 0,
      proxy_static_ip: pStaticIp,
      proxy_toggle: !!proxyToggle,
      type: "",
      session_id: "",
      pin: "",
      vendor_id: cId,
      sizing: parseInt(sizing) || 0,
      account_status: accountStatus,
    };

    // Wizard mode: save for approval
    if (isWizard && onWizardSubmit) {
      onWizardSubmit({
        client_id: cId,
        brokerData: { ...payload, account_status: accountStatus, sizing: parseInt(sizing) || 0 },
      });
      return;
    }

    console.log("Polaris Nubra payload:", payload);

    setIsSubmitting(true);
    apiClientChoice
      .request({
        method: "post",
        url: "https://choice.server.sypto.xyz/add/user",
        headers: { "Content-Type": "application/json" },
        data: JSON.stringify(payload),
      })
      .then((response) => {
        console.log("Choice server response:", response.data);
        toast.success("Nubra (Polaris) user added successfully!", { duration: 5000 });
        if (handleAddNubraUserIndexMuliplier) {
          handleAddNubraUserIndexMuliplier(cId, nm, 0, 0, 0, 0, 0);
        }
        onClose(true);
      })
      .catch((error) => {
        console.error("Failed to add Polaris Nubra user:", error);
        toast.error("Failed to add user");
        onClose(false);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // ── Standard (Godseye) submit ─────────────────────────────────────────────
  const handleAddNubraUsers = () => {
    const cId = (clientId || "").trim();
    const nm = (name || "").trim();
    const em = (email || "").trim();
    const totp = (totpKey || "").trim();
    const p = (pin || "").trim();
    const st = (status || "").trim();
    const ph = (phoneNumber || "").trim();
    const lbl = (label || "").trim();
    const pUser = (proxyUser || "").trim();
    const pPwd = (proxyPassword || "").trim();
    const pHost = (proxyHost || "").trim();
    const pStaticIp = (proxyStaticIp || "").trim();

    const data = {
      client_id: cId,
      name: nm,
      email: em,
      totp_key: totp,
      pin: p,
      status: st,
      phone_number: ph,
      label: lbl,
      proxy_user: pUser,
      proxy_password: pPwd,
      proxy_host: pHost,
      proxy_port: parseInt(proxyPort) || 0,
      proxy_toggle: !!proxyToggle,
      proxy_static_ip: pStaticIp || null,
    };

    if (
      !data.client_id ||
      !data.name ||
      !data.email ||
      !data.totp_key ||
      !data.pin ||
      !data.status ||
      !data.phone_number ||
      !data.label ||
      (multiplier === "" || multiplier === null) ||
      (bankniftyMultiplier === "" || bankniftyMultiplier === null) ||
      (finniftyMultiplier === "" || finniftyMultiplier === null) ||
      (midcpniftyMultiplier === "" || midcpniftyMultiplier === null) ||
      (sensexMultiplier === "" || sensexMultiplier === null)
    ) {
      console.log("Missing required fields:", data);
      toast.error("Missing required fields !!", { duration: 5000 });
      return;
    }

    // Wizard mode: save for approval
    if (isWizard && onWizardSubmit) {
      onWizardSubmit({
        client_id: data.client_id,
        brokerData: {
          ...data,
          multiplier: parseInt(multiplier) || 0,
          banknifty_multiplier: parseInt(bankniftyMultiplier) || 0,
          finnifty_multiplier: parseInt(finniftyMultiplier) || 0,
          midcpnifty_multiplier: parseInt(midcpniftyMultiplier) || 0,
          sensex_multiplier: parseInt(sensexMultiplier) || 0,
        },
      });
      return;
    }

    console.log("New Nubra User Data", data);

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}add-nubra-user`,
      headers: { "Content-Type": "application/json" },
      data: JSON.stringify(data),
    };

    setIsSubmitting(true);
    apiClient
      .request(config)
      .then((response) => {
        console.log("Server response:", response.data);
        if (response.data.status === 200) {
          toast.success("New Nubra user added successfully!", { duration: 5000 });
          handleAddNubraUserIndexMuliplier(cId, nm, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier);
          onClose(true);
        } else {
          onClose(false);
        }
      })
      .catch((error) => {
        console.error("Failed to add new Nubra user", error);
        onClose(false);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // ── Polaris (Choice-server) form ─────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  if (isPolarisOrPolarisLite) {
    return (
      <div className={isWizard ? "font-inter space-y-4" : "font-inter space-y-4 max-h-[60vh] overflow-y-auto custom-scroll pr-4"}>
        <div className="grid grid-cols-4 gap-6 py-4">
          {/* Client ID */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Client Id <span className="text-red-500">*</span></Label>
            <Input className="text-white" type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} />
          </div>

          {/* Name */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Name <span className="text-red-500">*</span></Label>
            <Input className="text-white" type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          {/* Email */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Email <span className="text-red-500">*</span></Label>
            <Input className="text-white" type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          {/* Phone */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Phone</Label>
            <Input className="text-white" type="text" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
          </div>

          {/* Password */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Password <span className="text-red-500">*</span></Label>
            <div className="relative">
              <Input className="text-white pr-10" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* TOTP Key */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">TOTP Key <span className="text-red-500">*</span></Label>
            <div className="relative">
              <Input className="text-white pr-10" type={showTotpKey ? "text" : "password"} value={totpKey} onChange={(e) => setTotpKey(e.target.value)} />
              <button type="button" onClick={() => setShowTotpKey(!showTotpKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                {showTotpKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* DOB */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Date of Birth</Label>
            <Input className="text-white" type="text" placeholder="YYYY-MM-DD" value={dob} onChange={(e) => setDob(e.target.value)} />
          </div>

          {/* Sizing */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Sizing <span className="text-red-500">*</span></Label>
            <Input className="text-white" type="number" value={sizing} onChange={(e) => setSizing(e.target.value)} />
          </div>

          {/* Account Status */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Account Status <span className="text-red-500">*</span></Label>
            <Select value={accountStatus} onValueChange={(val) => setAccountStatus(val)} disabled={isOnholdLegality && !isLegalitiesComplete}>
              <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
                <SelectValue placeholder="Select Status" />
              </SelectTrigger>
              <SelectContent className="border-zinc-700">
                <SelectGroup>
                  {accountStatusOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Proxy User */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Proxy User</Label>
            <Input className="text-white" type="text" value={proxyUser} onChange={(e) => setProxyUser(e.target.value)} />
          </div>

          {/* Proxy Password */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Proxy Password</Label>
            <div className="relative">
              <Input className="text-white pr-10" type={showProxyPassword ? "text" : "password"} value={proxyPassword} onChange={(e) => setProxyPassword(e.target.value)} />
              <button type="button" onClick={() => setShowProxyPassword(!showProxyPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                {showProxyPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Proxy Host */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Proxy Host</Label>
            <Input className="text-white" type="text" value={proxyHost} onChange={(e) => setProxyHost(e.target.value)} />
          </div>

          {/* Proxy Port */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Proxy Port</Label>
            <Input className="text-white" type="number" value={proxyPort} onChange={(e) => setProxyPort(e.target.value)} />
          </div>

          {/* Proxy Static IP */}
          <div className="grid grid-cols-1 items-center gap-4">
            <Label className="text-white">Proxy Static IP</Label>
            <Input className="text-white" type="text" value={proxyStaticIp} onChange={(e) => setProxyStaticIp(e.target.value)} />
          </div>

          {/* Proxy Toggle */}
          <div className="flex items-center space-x-2 h-full pt-4">
            <Checkbox id="proxy-toggle-polaris-nubra" checked={proxyToggle} onCheckedChange={(checked) => setProxyToggle(checked)} />
            <Label htmlFor="proxy-toggle-polaris-nubra" className="text-white cursor-pointer">Proxy Toggle</Label>
          </div>
        </div>

        <div className="mt-6 flex justify-end pb-4">
          <Button
            disabled={isSubmitting || !clientId || !name || !email || !totpKey || !password}
            type="submit"
            className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
            onClick={handleAddPolarisChoiceNubraUser}
          >
            {isSubmitting ? "Adding..." : isWizard ? "Submit for Approval" : "Add Nubra (Polaris) User"}
          </Button>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ── Standard (Godseye) form ───────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="font-inter space-y-4">
      <div className="grid grid-cols-4 gap-6 py-4">
        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Client Id <span className="text-red-500">*</span></Label>
          <Input className="text-white" type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Name <span className="text-red-500">*</span></Label>
          <Input className="text-white" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">TOTP Key <span className="text-red-500">*</span></Label>
          <div className="relative">
            <Input className="text-white pr-10" type={showTotpKey ? "text" : "password"} value={totpKey} onChange={(e) => setTotpKey(e.target.value)} />
            <button type="button" onClick={() => setShowTotpKey(!showTotpKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
              {showTotpKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Pin <span className="text-red-500">*</span></Label>
          <div className="relative">
            <Input className="text-white pr-10" type={showPin ? "text" : "password"} value={pin} onChange={(e) => setPin(e.target.value)} />
            <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
              {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Email <span className="text-red-500">*</span></Label>
          <Input className="text-white" type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Status <span className="text-red-500">*</span></Label>
          <Select value={status} onValueChange={(val) => setStatus(val)} disabled={isOnholdLegality && !isLegalitiesComplete}>
            <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
              <SelectValue placeholder="Select Status" />
            </SelectTrigger>
            <SelectContent className="border-zinc-700">
              <SelectGroup>
                {statusOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Phone Number <span className="text-red-500">*</span></Label>
          <Input className="text-white" type="text" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Label <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="text"
            list="label-options"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Select or type label"
          />
          <datalist id="label-options">
            {labelOptions.map((l) => (
              <option key={l.value} value={l.value} />
            ))}
          </datalist>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Multiplier Number <span className="text-red-500">*</span></Label>
          <Input className="text-white" type="number" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Bank Nifty Multiplier <span className="text-red-500">*</span></Label>
          <Input className="text-white" type="number" value={bankniftyMultiplier} onChange={(e) => setBankniftyMultiplier(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Fin Nifty Multiplier <span className="text-red-500">*</span></Label>
          <Input className="text-white" type="number" value={finniftyMultiplier} onChange={(e) => setFinniftyMultiplier(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Midcap Nifty Multiplier <span className="text-red-500">*</span></Label>
          <Input className="text-white" type="number" value={midcpniftyMultiplier} onChange={(e) => setMidcpniftyMultiplier(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Sensex Multiplier <span className="text-red-500">*</span></Label>
          <Input className="text-white" type="number" value={sensexMultiplier} onChange={(e) => setSensexMultiplier(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Proxy User</Label>
          <Input className="text-white" type="text" value={proxyUser} onChange={(e) => setProxyUser(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Proxy Password</Label>
          <div className="relative">
            <Input className="text-white pr-10" type={showProxyPassword ? "text" : "password"} value={proxyPassword} onChange={(e) => setProxyPassword(e.target.value)} />
            <button type="button" onClick={() => setShowProxyPassword(!showProxyPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
              {showProxyPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Proxy Host</Label>
          <Input className="text-white" type="text" value={proxyHost} onChange={(e) => setProxyHost(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Proxy Port</Label>
          <Input className="text-white" type="number" value={proxyPort} onChange={(e) => setProxyPort(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Proxy Static IP</Label>
          <Input
            className="text-white"
            type="text"
            placeholder="e.g. 2402:1f00:8300:989::1"
            value={proxyStaticIp}
            onChange={(e) => setProxyStaticIp(e.target.value)}
          />
        </div>

        <div className="flex items-center space-x-2 h-full pt-4">
          <Checkbox
            id="proxy-toggle"
            checked={proxyToggle}
            onCheckedChange={(checked) => setProxyToggle(checked)}
          />
          <Label htmlFor="proxy-toggle" className="text-white cursor-pointer">
            Proxy Toggle
          </Label>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          disabled={
            isSubmitting ||
            (clientId === "" || clientId === null) ||
            (name === "" || name === null) ||
            (pin === "" || pin === null) ||
            (status === "" || status === null) ||
            (totpKey === "" || totpKey === null) ||
            (email === "" || email === null) ||
            (phoneNumber === "" || phoneNumber === null) ||
            (label === "" || label === null) ||
            (multiplier === "" || multiplier === null) ||
            (bankniftyMultiplier === "" || bankniftyMultiplier === null) ||
            (finniftyMultiplier === "" || finniftyMultiplier === null) ||
            (midcpniftyMultiplier === "" || midcpniftyMultiplier === null) ||
            (sensexMultiplier === "" || sensexMultiplier === null)
          }
          type="submit"
          className="text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
          onClick={handleAddNubraUsers}
        >
          {isSubmitting ? "Adding..." : isWizard ? "Submit for Approval" : "Add Nubra User"}
        </Button>
      </div>
    </div>
  );
}